import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { StaticSRPHeader } from './StaticSRPHeader';
import { HitCompactView } from '../../../components/Hit/HitCompactView';
import { buildHitCompactViewPropsFromStatic } from './buildHitCompactViewProps';
import type { StaticSRPProps } from '../../../types/srp-static';
import type { Locale } from '../../../utils/routing/generate-url';
import { generateUrlFromIndexUiState } from '../../../utils/algolia/url-handling';
import Footer from '../../../components/Footer';

/** Number of job cards to fully render on the server; the rest are placeholders to reduce render+serialize time. */
const INITIAL_SSR_HITS = 12;

/**
 * Static SRP: no SearchProvider, FacetsProvider, or SavedState.
 * All data from props. Renders same layout as interactive SRP to avoid CLS.
 * When initialResults is null and useProgressiveSrp is true, shows a simple loading message (slim payload case).
 */
export function StaticSRPPage({ data, locale, pathname }: StaticSRPProps): React.ReactElement {
  const intl = useIntl();
  const results = data.initialResults;
  const hits = results?.hits ?? [];
  const nbHits = results?.nbHits ?? 0;
  const page = data.initialPage ?? 0;
  const nbPages = results?.nbPages ?? 1;
  const hasNoResults = (data.hasNoResults ?? false) || nbHits === 0;

  const isSlimPayload = !results && (data as { useProgressiveSrp?: boolean }).useProgressiveSrp === true;

  const localeTyped = locale as Locale;
  const filtersObj = typeof data.initialFilters === 'string'
    ? (() => { try { return JSON.parse(data.initialFilters) as Record<string, string[]>; } catch { return {}; } })()
    : (data.initialFilters ?? {});

  const initialHits = hits.slice(0, INITIAL_SSR_HITS);
  const placeholderCount = Math.max(0, hits.length - INITIAL_SSR_HITS);

  return (
    <div className="relative flex flex-col h-screen mobile-viewport-height overflow-hidden lg:overflow-visible">
      <StaticSRPHeader
        locale={locale}
        query={data.initialQuery ?? ''}
        facetsData={data.facetsData as Record<string, Array<{ value: string; label?: string; count: number; isRefined?: boolean }>> | undefined}
        refinementList={filtersObj}
        pathname={pathname}
        indexName={data.indexName ?? `${locale}_search`}
      />
      <div className="flex-1 flex lg:flex-row lg:relative lg:overflow-hidden min-h-0">
        <div className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-x-hidden overflow-y-auto min-h-0 pt-1 sm:pt-1 md:pt-2 pb-20 lg:pb-0 sm:px-0 md:px-2 lg:px-8">
          <div className="w-full bg-gray-50 dark:bg-gray-900 pt-1 sm:pt-1 md:pt-2">
            <div className="max-w-screen-2xl mx-auto sm:px-0 md:px-2 lg:px-8">
              <div className="ais-InstantSearch @container w-full min-w-0 pt-1 sm:pt-1 md:pt-2 flex flex-col gap-6">
                {/* Sidebar: job count only */}
                <aside
                  id="search-controls"
                  className="search-controls text-black dark:text-white px-2 @sm:px-2 @md:px-4 pb-2 @sm:pb-2 @md:pb-4 flex flex-col gap-2 @md:gap-3"
                >
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {isSlimPayload ? null : (
                      <FormattedMessage
                        id="search.jobCount"
                        defaultMessage="{count, number} jobs"
                        description="Number of job results"
                        values={{ count: nbHits }}
                      />
                    )}
                  </p>
                </aside>

                <main id="job-results" className="job-results">
                  <section
                    className="srp-results"
                    aria-labelledby="results-title"
                  >
                    <h2 id="results-title" className="sr-only">
                      <FormattedMessage id="search.results.title" defaultMessage="Job Search Results" description="Screen reader heading for the job search results section" />
                    </h2>
                    {isSlimPayload ? (
                      /* No loading UI – static shell should not render until results are ready */
                      <div aria-busy="true" />
                    ) : hasNoResults ? (
                      <p className="text-gray-600 dark:text-gray-400 py-8">
                        <FormattedMessage id="search.noResults" defaultMessage="No jobs found." description="No results message" />
                      </p>
                    ) : (
                      <ul className="srp-results-grid">
                        {initialHits.map((hit, index) => (
                          <li
                            key={(hit.objectID ?? hit.id) as string ?? index}
                            className="srp-results-grid-item"
                            style={index > 8 ? { contentVisibility: 'auto', containIntrinsicSize: '1px 150px' } : undefined}
                          >
                          <HitCompactView
                            {...buildHitCompactViewPropsFromStatic(
                              hit as Record<string, unknown>,
                              index,
                              data,
                              localeTyped,
                              pathname
                            )}
                          />
                        </li>
                      ))}
                      {Array.from({ length: placeholderCount }, (_, i) => (
                        <li
                          key={`placeholder-${initialHits.length + i}`}
                          className="srp-results-grid-item"
                          style={{ contentVisibility: 'auto', containIntrinsicSize: '1px 150px', minHeight: 150 }}
                          aria-hidden
                        />
                      ))}
                      </ul>
                    )}
                  </section>
                </main>

                {/* Minimal pagination: next/prev links (hidden when loading slim payload) */}
                {!isSlimPayload && nbPages > 1 && (() => {
                  const filtersObj = typeof data.initialFilters === 'string'
                    ? (() => { try { return JSON.parse(data.initialFilters) as Record<string, string[]>; } catch { return {}; } })()
                    : (data.initialFilters ?? {});
                  const prevUrl = page > 0
                    ? generateUrlFromIndexUiState(
                        { query: data.initialQuery ?? '', refinementList: filtersObj, page: page - 1 },
                        data.indexName ?? `${locale}_search`,
                        pathname
                      )
                    : null;
                  const nextUrl = page < nbPages - 1
                    ? generateUrlFromIndexUiState(
                        { query: data.initialQuery ?? '', refinementList: filtersObj, page: page + 1 },
                        data.indexName ?? `${locale}_search`,
                        pathname
                      )
                    : null;
                  return (
                    <nav className="flex items-center justify-center gap-4 py-4" aria-label="Pagination">
                      {prevUrl && (
                        <a href={prevUrl} className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
                          ← Previous
                        </a>
                      )}
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Page {page + 1} of {nbPages}
                      </span>
                      {nextUrl && (
                        <a href={nextUrl} className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
                          Next →
                        </a>
                      )}
                    </nav>
                  );
                })()}
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
      {/* Mobile bottom bar placeholder to reserve space */}
      <div className="lg:hidden h-14 flex-shrink-0 bg-gray-100 dark:bg-gray-800" aria-hidden />
    </div>
  );
}
